import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Typography, Tooltip, Modal, Button, Slider, IconButton } from '@mui/material';
import { InfoOutlinedIcon, InsertPhotoOutlinedIcon, CloseIcon } from 'icons/materialIcons';
import Cropper from 'react-easy-crop';
import getCroppedImg from 'team/shared/utils/images/teamCropImageUtils';

const TeamUploadImage = ({ onImageSelect }) => {
    const uploadInputRef = useRef(null);
    const [image, setImage] = useState(null);
    const [open, setOpen] = useState(false);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [croppedImage, setCroppedImage] = useState(null);
    const [objectUrl, setObjectUrl] = useState(null);
    const [inputDisabled, setInputDisabled] = useState(false);

    const calculateInitialZoom = useCallback((mediaWidth, mediaHeight) => {
        return 1; // Always return 1 to show image at its original size
    }, []);

    const onMediaLoaded = useCallback(() => {
        setZoom(1); // Always start at original size
        setCrop({ x: 0, y: 0 });
    }, []);

    const handleUploadClick = () => {
        uploadInputRef.current.click();
    };

    const handleUploadChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setObjectUrl(url);
            setImage(url);
            setOpen(true);
            event.target.value = '';
        }
    };

    const handleUploadDrop = (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setObjectUrl(url);
            setImage(url);
            setOpen(true);
        }
    };

    const handleUploadDragOver = (event) => {
        event.preventDefault();
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleClose = () => {
        setOpen(false);
        setImage(null);
        setObjectUrl(null);
        setZoom(1);
        setCroppedImage(null); // Clear the cropped image
    };
    const handleCropSave = async () => {
        try {
            const croppedImageBlob = await getCroppedImg(image, croppedAreaPixels, 'rectangle', 'uploaded-image');
            const croppedImageUrl = URL.createObjectURL(croppedImageBlob);
            setCroppedImage(croppedImageUrl);
            onImageSelect(croppedImageUrl);
        } catch (e) {
            console.error(e);
        } finally {
            setOpen(false); // Stay in the "upload image" state
            setImage(null); // Clear the image
            setCroppedAreaPixels(null); // Reset the cropped area
            setCrop({ x: 0, y: 0 }); // Reset the crop position
            setZoom(1); // Reset the zoom
        }
    };

    // Clean up object URLs when component unmounts
    useEffect(() => {
        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [objectUrl]);

    return (
        <>
            <Box
                onClick={handleUploadClick}
                onDrop={handleUploadDrop}
                onDragOver={handleUploadDragOver}
                sx={{
                    border: '2px dashed #e0e0e0',
                    borderRadius: '12px',
                    padding: 2,
                    textAlign: 'center',
                    width: '100%',
                    cursor: 'pointer',
                    margin: '20px auto',
                }}
            >
                <input
                    type="file"
                    accept="image/*"
                    ref={uploadInputRef}
                    style={{ display: 'none' }}
                    onChange={handleUploadChange}
                />

                <Box
                    sx={{
                        borderRadius: '8px',
                        padding: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Box
                        sx={{
                            backgroundColor: '#f7f7f7',
                            borderRadius: '8px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: '10px 25px',
                            marginBottom: '20px',
                        }}
                    >
                        <InsertPhotoOutlinedIcon sx={{ fontSize: 40, color: '#cccccc' }} />
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            <Typography
                                variant="body1"
                                sx={{ color: '#2aaff7', fontWeight: '700', fontSize: '20px' }}
                            >
                                Select Image
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{ color: 'black', fontWeight: 'bold', fontSize: '16px', marginLeft: '6px' }}
                            >
                                to Upload
                            </Typography>
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{ color: 'black', fontSize: '14px', marginTop: 1 }}
                        >
                            or drag and drop it here
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {open && (
                <Modal
                    open={open}
                    onClose={handleClose}
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backdropFilter: 'blur(7px)',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    }}
                >
                    <Box
                        sx={{
                            outline: 'none',
                            width: '90vw',
                            maxWidth: '900px',
                            height: '90vh',
                            maxHeight: '700px',
                        }}
                    >
                        <Box
                            sx={{
                                position: 'relative',
                                width: '100%',
                                height: 'calc(100% - 100px)',
                                marginBottom: 2,
                            }}
                        >
                            <Cropper
                                image={image}
                                crop={crop}
                                zoom={zoom}
                                aspect={2.6}
                                cropShape="rect"
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                                onMediaLoaded={onMediaLoaded}
                                objectFit="contain"
                                centerImage={true}
                                showGrid={false}
                                minZoom={0.05}
                                maxZoom={3}
                                restrictPosition={false}
                            />
                        </Box>
                        <Box sx={{ textAlign: 'center', mb: 2 }}>
                            <Slider
                                value={zoom}
                                onChange={(e, newValue) => setZoom(newValue)}
                                min={0.05}
                                max={3}
                                step={0.01}
                                defaultValue={1.5}
                                valueLabelDisplay="auto"
                            />
                            <Box sx={{ mt: 2 }}>
                                <Button onClick={handleClose} variant="contained" sx={{ mr: 2 }}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={() => {
                                        handleCropSave();
                                        setOpen(false);
                                    }}
                                    variant="contained"
                                    color="primary"
                                >
                                    Crop
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Modal>
            )}
        </>
    );
};

export default TeamUploadImage;